import { useState } from "react"
import { StickyNote, Pencil, Trash2 } from "lucide-react"
import type { ClientTheme } from "@/app/lib/themes"

interface NotesSectionProps {
  /** Current saved note, or null when none exists. */
  note: string | null
  /** Owner/admin get the editor; members see the note read-only. */
  canEdit: boolean
  /** Must reject on failure so the editor stays open with the draft intact. */
  onSave: (body: string) => Promise<unknown>
  onRemove: () => void
  /** Disables the editor buttons while a save/remove is in flight. */
  isMutating: boolean
  /** Server error message from the last failed save/remove, if any. */
  errorMessage?: string | null
  theme: ClientTheme
  title?: string
}

/**
 * A section-note panel: amber "annotated ledger" card showing the saved note,
 * with inline add/edit/remove for writers. Presentation-only — the parent owns
 * the mutations — so it can back notes on any subject (STRs now, wells later).
 *
 * Edit state is plain local state driven by button handlers; the draft is
 * seeded when editing starts, so no effects are needed to sync props.
 */
export function NotesSection({
  note,
  canEdit,
  onSave,
  onRemove,
  isMutating,
  errorMessage,
  theme,
  title = "Section Note",
}: NotesSectionProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")

  // Nothing to show members when no note exists.
  if (!note && !canEdit) return null

  const startEditing = () => {
    setDraft(note ?? "")
    setEditing(true)
  }

  const submit = () => {
    const body = draft.trim()
    if (!body) return
    // Close only on success; on failure the draft stays editable and the
    // parent's errorMessage explains what happened.
    onSave(body).then(
      () => setEditing(false),
      () => {}
    )
  }

  const remove = () => {
    onRemove()
    setEditing(false)
  }

  const smallButton = (
    label: React.ReactNode,
    onClick: () => void,
    opts?: { accent?: boolean; disabled?: boolean }
  ) => (
    <button
      type="button"
      onClick={onClick}
      disabled={opts?.disabled}
      className="flex items-center gap-1 rounded-md"
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "11px",
        fontWeight: 600,
        padding: "4px 10px",
        color: opts?.accent ? theme.textOnBrand : theme.textMuted,
        backgroundColor: opts?.accent ? theme.brandPrimary : theme.bgSurface2,
        border: `1px solid ${opts?.accent ? theme.brandPrimary : theme.borderColor}`,
        cursor: opts?.disabled ? "default" : "pointer",
        opacity: opts?.disabled ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  )

  return (
    <>
      <div
        className="flex items-center justify-between px-6 py-3"
        style={{ borderBottom: `1px solid ${theme.borderColor}` }}
      >
        <h3
          className="flex items-center gap-1.5"
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "13px",
            fontWeight: 600,
            color: theme.textPrimary,
          }}
        >
          <StickyNote size={13} style={{ color: theme.noteAccent }} />
          {title}
        </h3>
        {canEdit && !editing && (
          <div className="flex items-center gap-1.5">
            {note &&
              smallButton(
                <>
                  <Trash2 size={11} /> Remove
                </>,
                remove,
                { disabled: isMutating }
              )}
            {smallButton(
              <>
                <Pencil size={11} /> {note ? "Edit" : "Add note"}
              </>,
              startEditing,
              { disabled: isMutating }
            )}
          </div>
        )}
      </div>

      <div className="px-6 py-3" style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Add a note for this section…"
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                lineHeight: 1.5,
                color: theme.textPrimary,
                backgroundColor: theme.bgSurface2,
                border: `1px solid ${theme.borderColor}`,
                borderRadius: "8px",
                padding: "8px 10px",
                resize: "vertical",
                outline: "none",
              }}
            />
            <div className="flex items-center gap-1.5">
              {smallButton("Save", submit, { accent: true, disabled: isMutating || !draft.trim() })}
              {smallButton("Cancel", () => setEditing(false), { disabled: isMutating })}
            </div>
          </div>
        ) : note ? (
          <div
            className="rounded-lg px-3 py-2.5"
            style={{
              backgroundColor: `${theme.noteAccent}14`,
              border: `1px solid ${theme.noteAccent}38`,
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: "12px",
                lineHeight: 1.55,
                color: theme.textPrimary,
                whiteSpace: "pre-wrap",
              }}
            >
              {note}
            </div>
          </div>
        ) : (
          <div style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}>
            No note for this section yet.
          </div>
        )}
        {errorMessage && (
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "11px",
              color: theme.brandAccent,
              marginTop: "6px",
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </>
  )
}
